import { documentBusinessTypesEnum } from "helpers/enums";
import { filterByDocumentCreationStatus, normalizeDocumentListParams } from "helpers/documentLists";
import { OdataFilters, queryCollection, queryParams } from "helpers/reqparams";
import { alertRequest, body, downloadFileHandler, request } from "services/request";

const resource = "api/documents/universalDocuments";
const resourceQuery = { orderBy: "id desc" };
export const universalTransferDocumentGroupType = "UniversalTransferDocument";
type DocumentListParams = OdataFilters & {
    type: UniversalDocumentType;
    startedAt?: string;
    endedAt?: string;
    documentCreationStatus?: string;
};
type UniversalDocumentType =
    | "Contract"
    | "Invoice"
    | "AcceptanceCertificate"
    | "TaxInvoice"
    | typeof universalTransferDocumentGroupType
    | "UniversalTransferDocumentStatus1"
    | "UniversalTransferDocumentStatus2";

const hasFilter = (filter?: {}) => !!filter && Object.keys(filter).length > 0;
const universalTransferDocumentTypeFilter = {
    or: [
        { type: documentBusinessTypesEnum.UniversalTransferDocumentStatus1 },
        { type: documentBusinessTypesEnum.UniversalTransferDocumentStatus2 },
    ],
};
const getTypeFilter = (type: UniversalDocumentType, preparedFilter?: {}) => {
    const typeFilter = type === universalTransferDocumentGroupType ? universalTransferDocumentTypeFilter : { type };

    return hasFilter(preparedFilter) ? { and: [preparedFilter, typeFilter] } : typeFilter;
};

const compactParams = (params: { [key: string]: unknown }) =>
    Object.keys(params).reduce(
        (acc, key) => {
            const value = params[key];
            return value === undefined || value === null || value === "" ? acc : { ...acc, [key]: value };
        },
        {} as { [key: string]: unknown },
    );

const normalizeUniversalDocumentListItem = (item: { [key: string]: any }) => {
    const universalDocumentId = item.universalDocumentId ?? item.id ?? null;
    const isGenerating = item.isGenerating === true;

    return {
        ...item,
        id: universalDocumentId,
        universalDocumentId,
        documentExists: !!universalDocumentId || isGenerating,
        files: item.files || [],
        sendAt: item.sendAt ?? item.sentAt,
        sentAt: item.sentAt ?? item.sendAt,
    };
};

const toListPayload = <T extends { documentExists?: boolean }>(items: T[], documentCreationStatus?: string) => {
    const data = filterByDocumentCreationStatus(items, documentCreationStatus);

    return { data, count: data.length };
};

export const index = ({ type, startedAt, endedAt, documentCreationStatus }: DocumentListParams) => {
    const listParams = normalizeDocumentListParams({ startedAt, endedAt, documentCreationStatus });
    const query = queryParams(compactParams({ type, startedAt: listParams.startedAt, endedAt: listParams.endedAt }));

    return request(`${resource}/getList${query}`, { method: "GET" }).then((res) =>
        toListPayload((res || []).map(normalizeUniversalDocumentListItem), listParams.documentCreationStatus),
    );
};
export const getListForBid = ({
    preparedFilter,
    id,
    type,
    ...params
}: OdataFilters & {
    id: number;
    type: UniversalDocumentType;
}) => {
    const query = queryCollection(params, { filter: getTypeFilter(type, preparedFilter), ...resourceQuery });
    return request(`${resource}/getListForBid/${id}${query}`, { method: "GET" }, { countInHeaders: true });
};
export const getAllUniversalDocumentsForBid = (id: number) => {
    const query = queryCollection({}, resourceQuery);
    return request(`${resource}/getListForBid/${id}${query}`, { method: "GET" }, { countInHeaders: true });
};
export const getListForDistributionBid = ({
    preparedFilter,
    id,
    type,
    ...params
}: OdataFilters & {
    id: number;
    type: UniversalDocumentType;
}) => {
    const query = queryCollection(params, { filter: getTypeFilter(type, preparedFilter), ...resourceQuery });
    return request(`${resource}/getListForDistributionBid/${id}${query}`, { method: "GET" }, { countInHeaders: true });
};
export const getDocumentFile = (id: number, extension: "pdf" | "xml" | "docx" | "utd.xml") =>
    request(`${resource}/getDocumentFile?id=${id}&extension=${extension}`, { method: "GET" });
export const getItem = (id: number) => request(`${resource}/get/${id}`, { method: "GET" });
export const delItem = (params: {}) => alertRequest(`${resource}/delete`, body(params));
export const setSendStatus = (params: {}) => alertRequest(`${resource}/setSendStatus`, body(params));
export const setStatus = (params: {}) => alertRequest(`${resource}/setStatus`, body(params));
export const downloadArchive = (params: {}) =>
    request(`${resource}/downloadArchive${queryParams(params)}`, { method: "GET" }, { withoutMiddleware: true }).then(
        (res) => {
            let fileNameFromHeader = "files.zip";
            if (res.ok) {
                const disposition = res.headers.get("Content-Disposition");
                if (disposition && disposition.indexOf("attachment") !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        const filename = matches[1].replace(/['"]/g, "");
                        try {
                            fileNameFromHeader = decodeURI(filename);
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }
            }
            downloadFileHandler(res, fileNameFromHeader);
        },
    );
